import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button, Divider, Switch, FormControlLabel, MenuItem, Select, TextField, Autocomplete, Chip, CircularProgress } from "@mui/material";
import ContentBox from "../../components/ui/ContentBox";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../../api/Api";
import { Urls } from "../../api/Urls";
import { useAuthContext } from "../../hooks/useAuthContext";
import moment from "moment";
import BreadcrumComp from "../../components/ui/BreadcrumComp";

function MakeupRequestDetail() {
  const { user } = useAuthContext();
  const access_token = user?.access_token;
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [allSlots, setAllSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  // Filtres
  const [sessionType, setSessionType] = useState("");
  const [courtId, setCourtId] = useState("");
  const [coachId, setCoachId] = useState("");
  const [termId, setTermId] = useState("");
  const [date, setDate] = useState("");
  const [courts, setCourts] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [terms, setTerms] = useState([]);
  // Sélection multiple
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [proposalMode, setProposalMode] = useState(false);
  const lockedMessage = "This makeup request is locked because the player has already selected a slot.";

  useEffect(() => {
    document.title = "Moody admin - Makeup Request Details";
    fetchRequestDetails();
    fetchCourts();
    fetchCoaches();
    fetchTerms();
  }, [id]);

  const fetchRequestDetails = () => {
    setLoading(true);
    Api.get(Urls.GET_MAKEUP_REQUEST.replace(":id", id), {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((res) => {
        const requestData = res?.data?.data || res?.data;
        setRequest(requestData);
        
        // Si nous avons des slots IDs, récupérer leurs détails
        if (requestData.slots && requestData.slots.length > 0) {
          fetchSlotDetails(requestData.slots);
        }
        
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to fetch request details");
        setLoading(false);
      });
  };

  const fetchSlotDetails = (slotIds) => {
    setSlotsLoading(true);
    
    // Récupérer tous les slots en une seule requête
    Api.get("/courtSchedules/by-ids", {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      params: {
        ids: slotIds.join(',')
      },
    })
      .then((res) => {
        const slots = res?.data?.data || [];
        setAvailableSlots(slots);
      })
      .catch((err) => {
        console.error("Error fetching slot details:", err);
        toast.error("Failed to fetch slot details");
        setAvailableSlots([]);
      })
      .finally(() => setSlotsLoading(false));
  };

  const fetchCourts = () => {
    Api.get(Urls.GET_COURTS_LIST, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((res) => setCourts(res?.data?.data || []))
      .catch(() => setCourts([]));
  };
  const fetchCoaches = () => {
    Api.get(Urls.GET_COACHES, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((res) => setCoaches(res?.data?.data || []))
      .catch(() => setCoaches([]));
  };
  const fetchTerms = () => {
    Api.get(Urls.GET_TERMS_LIST, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then((res) => setTerms(res?.data?.data || []))
      .catch(() => setTerms([]));
  };

  const fetchAllSlots = () => {
    if (!showAllSlots) return;
    
    // All filters are now required
    if (!sessionType || !courtId || !coachId || !termId || !date) {
      toast.warning("Please select all filters to search for available slots");
      return;
    }
    
    // Convertir la date en format datetime - on ne veut que la date de début
    const selectedDate = moment(date);
    const startOfDay = selectedDate.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss');
    
    const params = {
      sessionType,
      court_id: courtId,
      coach_id: coachId,
      term_id: termId,
      startDatetime: startOfDay
      // On ne met pas endDatetime pour avoir tous les slots à partir de cette date
    };
    
    setSlotsLoading(true);
    Api.get("/courtSchedules", {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
      params: params,
    })
      .then((res) => {
        setAllSlots(res?.data?.data || []);
        if ((res?.data?.data || []).length === 0) {
          toast.info("No slots found with the selected filters");
        }
      })
      .catch((err) => {
        console.error("Error fetching slots:", err);
        toast.error("Failed to fetch slots. Please check your filters.");
        setAllSlots([]);
      })
      .finally(() => setSlotsLoading(false));
  };

  const handleSelectSlot = (slotId) => {
    if (request?.selected_slot) {
      toast.error(lockedMessage);
      return;
    }
    setActionLoading(true);
    Api.patch(Urls.SELECT_MAKEUP_SLOT_ADMIN.replace(":id", id), { slot_id: slotId }, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Slot assigned successfully");
        fetchRequestDetails();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to assign slot");
      })
      .finally(() => setActionLoading(false));
  };

  const handleSlotSelection = (slotId) => {
    setSelectedSlots(prev => {
      if (prev.includes(slotId)) {
        return prev.filter(id => id !== slotId);
      } else {
        return [...prev, slotId];
      }
    });
  };

  const handleMakeProposal = () => {
    if (request?.selected_slot) {
      toast.error(lockedMessage);
      return;
    }
    if (selectedSlots.length === 0) {
      toast.warning("Please select at least one slot");
      return;
    }

    setActionLoading(true);
    Api.patch(Urls.MAKE_PROPOSAL_MAKEUP_REQUEST.replace(":id", id), { 
      slot_ids: selectedSlots 
    }, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Proposal sent successfully");
        setSelectedSlots([]);
        setProposalMode(false);
        fetchRequestDetails();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to send proposal");
      })
      .finally(() => setActionLoading(false));
  };

  const handleCancelProposal = () => {
    setSelectedSlots([]);
    setProposalMode(false);
  };

  const handleCancelProposalRequest = () => {
    if (request?.selected_slot) {
      toast.error(lockedMessage);
      return;
    }
    setActionLoading(true);
    Api.patch(Urls.CANCEL_PROPOSAL_MAKEUP_REQUEST.replace(":id", id), {}, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Proposal cancelled successfully");
        fetchRequestDetails();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to cancel proposal");
      })
      .finally(() => setActionLoading(false));
  };

  const handleReject = () => {
    if (request?.selected_slot) {
      toast.error(lockedMessage);
      return;
    }
    setActionLoading(true);
    Api.patch(Urls.REJECT_MAKEUP_REQUEST.replace(":id", id), {}, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Request rejected successfully");
        navigate("/admin/makeup-requests");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to reject request");
      })
      .finally(() => setActionLoading(false));
  };

  const handleRefund = () => {
    if (request?.selected_slot) {
      toast.error(lockedMessage);
      return;
    }
    setActionLoading(true);
    Api.patch(Urls.REFUND_MAKEUP_REQUEST.replace(":id", id), {}, {
      headers: { "content-type": "application/json", authorization: `Bearer ${access_token}` },
    })
      .then(() => {
        toast.success("Request refunded successfully");
        navigate("/admin/makeup-requests");
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to refund request");
      })
      .finally(() => setActionLoading(false));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#FFA500';
      case 'approved':
        return '#4CAF50';
      case 'rejected':
        return '#F44336';
      case 'refunded':
        return '#9C27B0';
      default:
        return '#666D83';
    }
  };

  const formatSessionInfo = (session, title) => {
    if (!session) return null;
    return (
      <Box mb={2}>
        <Typography fontSize="16px" fontWeight="600" color="#4B465C" mb={1}>
          {title}
        </Typography>
        <Box p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
          <Typography fontWeight="500" fontSize="14px">
            {session.date_time ? moment(session.date_time).format("dddd, MMMM DD, YYYY") : `Session #${session.id}`}
          </Typography>
          {session.date_time && (
            <Typography fontSize="13px" color="#666D83" mt={0.5}>
              Time: {moment(session.date_time).format("HH:mm")}
            </Typography>
          )}
          {session.court && (
            <Typography fontSize="13px" color="#666D83">
              Court: {session.court.name}
            </Typography>
          )}
          {session.coach && (
            <Typography fontSize="13px" color="#666D83">
              Coach: {session.coach.first_name} {session.coach.last_name}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <Typography>Request not found</Typography>
      </Box>
    );
  }

  const isRequestLocked = !!request?.selected_slot;

  return (
    <Box display="flex" flexDirection="column" gap="26px" marginTop="26px">
      <ToastContainer />
      
      {/* Breadcrumb */}
      <Box>
        <BreadcrumComp 
          first="Makeup Requests" 
          second={`Request #${request.id}`}
          firstLink="/admin/makeup-requests"
        />
      </Box>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize="22px" fontWeight="500" lineHeight="30px" color="#4B465C">
          Makeup Request #{request.id}
        </Typography>
        <Chip 
          label={request.status_label?.toUpperCase() || request.status?.toUpperCase()} 
          sx={{ 
            backgroundColor: getStatusColor(request.status),
            color: 'white',
            fontWeight: '600'
          }}
        />
      </Box>
      {isRequestLocked && (
        <Box
          sx={{
            backgroundColor: "#FEECEC",
            border: "1px solid #F44336",
            borderRadius: "8px",
            padding: "10px 12px",
          }}
        >
          <Typography fontSize="14px" fontWeight="600" color="#F44336">
            This request is locked because the player has already selected a slot.
          </Typography>
        </Box>
      )}

      <ContentBox>
        <Box display="flex" flexDirection="column" gap="24px">
          
          {/* User and Player Information */}
          <Box>
            <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb={2}>
              User & Player Information
            </Typography>
            <Box display="flex" gap={4}>
              <Box flex={1}>
                <Typography fontSize="14px" fontWeight="600" color="#4B465C" mb={1}>
                  User
                </Typography>
                <Box p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <Typography fontWeight="500" fontSize="14px">
                    {request.user?.first_name} {request.user?.last_name}
                  </Typography>
                  <Typography fontSize="13px" color="#666D83">
                    {request.user?.email}
                  </Typography>
                  <Typography fontSize="13px" color="#666D83">
                    Phone: {request.user?.phone_number || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Box flex={1}>
                <Typography fontSize="14px" fontWeight="600" color="#4B465C" mb={1}>
                  Player
                </Typography>
                <Box p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <Typography fontWeight="500" fontSize="14px">
                    {request.player?.first_name} {request.player?.last_name}
                  </Typography>
                  <Typography fontSize="13px" color="#666D83">
                    Age: {request.player?.age || 'N/A'}
                  </Typography>
                  <Typography fontSize="13px" color="#666D83">
                    Level: <b>{request.player?.level?.name || 'N/A'}</b>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Session Information */}
          <Box>
            <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb={2}>
              Session Information
            </Typography>
            
            {/* Current Session */}
            {formatSessionInfo(request.current_session, "Current Session")}
            
            {/* New Session (if assigned) */}
            {request.selected_slot && formatSessionInfo(request.selected_slot, "New Assigned Session")}
            
            {!request.selected_slot && request.status_label === 'Pending' && (
              <Box>
                <Typography fontSize="16px" fontWeight="600" color="#4B465C" mb={1}>
                  New Session
                </Typography>
                <Typography fontSize="14px" color="#FFA500">
                  Pending assignment
                </Typography>
              </Box>
            )}
          </Box>

          <Divider />

          {!isRequestLocked ? (
            <>
              {/* Switch pour slots disponibles / tous les slots */}
              <Box mb={2}>
                <FormControlLabel
                  control={<Switch checked={showAllSlots} onChange={() => setShowAllSlots(!showAllSlots)} />}
                  label={showAllSlots ? "Show available slots only" : "Show all slots"}
                />
              </Box>
              {/* Filtres dynamiques */}
              {showAllSlots && (
                <Box>
                  <Typography fontSize="16px" fontWeight="600" color="#4B465C" mb={2}>
                    Filters
                  </Typography>
                  <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
                    <Autocomplete
                      options={["PROGRAM", "PRIVATE_LESSON", "COURT_BOOKING"]}
                      value={sessionType}
                      onChange={(event, newValue) => {
                        setSessionType(newValue || "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Session Type"
                          placeholder="Select session type"
                        />
                      )}
                      sx={{ minWidth: 180 }}
                    />
                    <Autocomplete
                      options={courts}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={courts.find(court => court.id === courtId) || null}
                      onChange={(event, newValue) => {
                        setCourtId(newValue ? newValue.id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Court"
                          placeholder="Select court"
                        />
                      )}
                      sx={{ minWidth: 180 }}
                    />
                    <Autocomplete
                      options={coaches}
                      getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={coaches.find(coach => coach.id === coachId) || null}
                      onChange={(event, newValue) => {
                        setCoachId(newValue ? newValue.id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Coach"
                          placeholder="Select coach"
                        />
                      )}
                      sx={{ minWidth: 180 }}
                    />
                    <Autocomplete
                      options={terms}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={terms.find(term => term.id === termId) || null}
                      onChange={(event, newValue) => {
                        setTermId(newValue ? newValue.id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Term"
                          placeholder="Select term"
                        />
                      )}
                      sx={{ minWidth: 180 }}
                    />
                    <TextField
                      type="date"
                      value={date}
                      onChange={e => setDate(e.target.value)}
                      label="Date"
                      InputLabelProps={{ shrink: true }}
                      sx={{ 
                        minWidth: 150,
                        '& .MuiInputBase-input': {
                          padding: '10px 14px',
                          fontSize: '14px'
                        }
                      }}
                    />
                    <Button 
                      variant="contained" 
                      onClick={fetchAllSlots}
                      disabled={actionLoading || !sessionType || !courtId || !coachId || !termId || !date}
                      sx={{ 
                        minWidth: 120,
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                  <Typography fontSize="13px" color="#666D83" mb={2}>
                    ⚠️ Please select all filters (Session Type, Court, Coach, Term, and Date) to search for available slots
                  </Typography>
                </Box>
              )}
              {/* Slots à afficher selon le switch */}
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography fontSize="18px" fontWeight="600" color="#4B465C">
                    {showAllSlots ? "All Slots" : "Available Slots"}
                  </Typography>
                  {showAllSlots && (allSlots.length > 0) && (
                    <Box display="flex" gap={1}>
                      <Button
                        variant={proposalMode ? "outlined" : "contained"}
                        size="small"
                        onClick={() => setProposalMode(false)}
                        disabled={actionLoading || isRequestLocked}
                      >
                        Direct Assignment
                      </Button>
                      <Button
                        variant={proposalMode ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setProposalMode(true)}
                        disabled={actionLoading || isRequestLocked}
                      >
                        Make Proposal
                      </Button>
                    </Box>
                  )}
                </Box>
                {/* Affichage des slots sélectionnés en mode proposition */}
                {proposalMode && selectedSlots.length > 0 && (
                  <Box mb={2} p={2} sx={{ backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #3890E2' }}>
                    <Typography fontSize="14px" fontWeight="600" color="#4B465C" mb={1}>
                      Selected Slots ({selectedSlots.length})
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {selectedSlots.map(slotId => {
                        const slot = (showAllSlots ? allSlots : availableSlots).find(s => s.id === slotId);
                        return slot ? (
                          <Chip
                            key={slotId}
                            label={`${moment(slot.start_datetime).format("MMM DD HH:mm")} - ${slot.court?.name || 'Court'}`}
                            onDelete={() => handleSlotSelection(slotId)}
                            color="primary"
                            size="small"
                          />
                        ) : null;
                      })}
                    </Box>
                    <Box display="flex" gap={1} mt={1}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleMakeProposal}
                        disabled={actionLoading || isRequestLocked}
                      >
                        Send Proposal
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancelProposal}
                        disabled={actionLoading}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
                {slotsLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                    <CircularProgress size={20} />
                    <Typography ml={1}>Loading slots...</Typography>
                  </Box>
                ) : (showAllSlots ? allSlots : availableSlots).length === 0 ? (
                  <Typography fontSize="14px" color="#FFA500">No slots found</Typography>
                ) : (
                  <Box 
                    display="grid" 
                    gridTemplateColumns={{
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                      lg: "repeat(5, 1fr)",
                      xl: "repeat(6, 1fr)"
                    }}
                    gap={1.5}
                  >
                    {(showAllSlots ? allSlots : availableSlots).map((slot) => (
                      <Button
                        key={slot.id}
                        variant={proposalMode && selectedSlots.includes(slot.id) ? "contained" : "outlined"}
                        disabled={actionLoading || isRequestLocked}
                        onClick={() => {
                          if (proposalMode) {
                            handleSlotSelection(slot.id);
                          } else {
                            handleSelectSlot(slot.id);
                          }
                        }}
                        sx={{ 
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          padding: '12px',
                          borderColor: proposalMode && selectedSlots.includes(slot.id) ? '#3890E2' : '#DBDADE',
                          backgroundColor: proposalMode && selectedSlots.includes(slot.id) ? '#3890E2' : 'transparent',
                          color: proposalMode && selectedSlots.includes(slot.id) ? 'white' : 'inherit',
                          height: 'auto',
                          minHeight: '100px',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          '&:hover': {
                            borderColor: '#3890E2',
                            backgroundColor: proposalMode && selectedSlots.includes(slot.id) ? '#3890E2' : '#f0f8ff'
                          }
                        }}
                      >
                        <Box width="100%">
                          <Typography fontWeight="600" fontSize="13px" mb={0.5} color={proposalMode && selectedSlots.includes(slot.id) ? 'white' : '#4B465C'}>
                            {slot.label || slot.date_time || slot.start_datetime || `Slot #${slot.id}`}
                          </Typography>
                          {(slot.date_time || slot.start_datetime) && (
                            <Typography fontSize="11px" color={proposalMode && selectedSlots.includes(slot.id) ? 'rgba(255,255,255,0.8)' : '#666D83'} mb={0.5}>
                              {moment(slot.date_time || slot.start_datetime).format("MMM DD")}
                            </Typography>
                          )}
                          {(slot.date_time || slot.start_datetime) && (
                            <Typography fontSize="11px" color={proposalMode && selectedSlots.includes(slot.id) ? 'rgba(255,255,255,0.8)' : '#666D83'} mb={0.5}>
                              {moment(slot.date_time || slot.start_datetime).format("HH:mm")}
                            </Typography>
                          )}
                          {slot.court && (
                            <Typography fontSize="11px" color={proposalMode && selectedSlots.includes(slot.id) ? 'rgba(255,255,255,0.8)' : '#666D83'} mb={0.5}>
                              🏟️ {slot.court.name || slot.court_name}
                            </Typography>
                          )}
                          {slot.level_name && (
                            <Typography fontSize="11px" color={proposalMode && selectedSlots.includes(slot.id) ? 'rgba(255,255,255,0.8)' : '#666D83'} mb={0.5}>
                              📊 {slot.level_name}
                            </Typography>
                          )}
                          {slot.program_name && (
                            <Typography fontSize="11px" color={proposalMode && selectedSlots.includes(slot.id) ? 'rgba(255,255,255,0.8)' : '#666D83'}>
                              🎾 {slot.program_name}
                            </Typography>
                          )}
                        </Box>
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Box p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px dashed #DBDADE' }}>
              <Typography fontSize="14px" color="#666D83">
                Slot management is disabled because the player has already selected a session.
              </Typography>
            </Box>
          )}

          {/* Request Details */}
          <Box>
            <Typography fontSize="18px" fontWeight="600" color="#4B465C" mb={2}>
              Request Details
            </Typography>
            <Box p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <Typography fontSize="13px" color="#666D83">
                Created: {request.created_at ? moment(request.created_at).format("dddd, MMMM DD, YYYY HH:mm") : 'N/A'}
              </Typography>
              {request.updated_at && (
                <Typography fontSize="13px" color="#666D83">
                  Updated: {moment(request.updated_at).format("dddd, MMMM DD, YYYY HH:mm")}
                </Typography>
              )}
              {request.refund_amount && (
                <Typography fontSize="13px" color="#666D83">
                  Refund Amount: ${request.refund_amount}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Actions */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={() => navigate("/admin/makeup-requests")}
              disabled={actionLoading}
            >
              Back to List
            </Button>
            {request.status === 'proposed' && !isRequestLocked && (
              <Button 
                variant="outlined" 
                color="warning"
                onClick={handleCancelProposal}
                disabled={actionLoading}
              >
                Cancel Proposal
              </Button>
            )}
            {!isRequestLocked && (
              <Button 
                variant="contained" 
                color="error"
                onClick={handleReject}
                disabled={actionLoading}
              >
                Reject Request
              </Button>
            )}
            {!isRequestLocked && request.status !== 'refunded' && request.status !== 'rejected' && (
              <Button 
                variant="contained" 
                color="secondary"
                onClick={handleRefund}
                disabled={actionLoading}
              >
                Refund Request
              </Button>
            )}
          </Box>
        </Box>
      </ContentBox>
    </Box>
  );
}

export default MakeupRequestDetail; 